import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AudioPlayer.css';

const VISUALIZER_BARS = 18;
const AUDIO_PLAYER_SYNC_EVENT = 'portfolio:audio-player-play';

const formatTime = (value) => {
  if (!Number.isFinite(value) || value < 0) {
    return '0:00';
  }

  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const AudioPlayer = ({ src, title }) => {
  const audioRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const barRefs = useRef([]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [visualizerEnabled, setVisualizerEnabled] = useState(false);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bars = useMemo(() => Array.from({ length: VISUALIZER_BARS }), []);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return undefined;
    }

    const syncState = () => {
      setCurrentTime(audio.currentTime || 0);
      setDuration(audio.duration || 0);
      setIsReady(Boolean(audio.duration));
      setIsPlaying(!audio.paused && !audio.ended);
      setVisualizerEnabled(!audio.paused && !audio.ended);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setVisualizerEnabled(false);
    };

    syncState();

    audio.addEventListener('loadedmetadata', syncState);
    audio.addEventListener('durationchange', syncState);
    audio.addEventListener('timeupdate', syncState);
    audio.addEventListener('play', syncState);
    audio.addEventListener('pause', syncState);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', syncState);
      audio.removeEventListener('durationchange', syncState);
      audio.removeEventListener('timeupdate', syncState);
      audio.removeEventListener('play', syncState);
      audio.removeEventListener('pause', syncState);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || typeof window === 'undefined') {
      return undefined;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) {
      return undefined;
    }

    let cancelled = false;

    const initializeVisualizer = async () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContextClass();
        }

        const audioContext = audioContextRef.current;

        if (!sourceRef.current) {
          sourceRef.current = audioContext.createMediaElementSource(audio);
        }

        if (!analyserRef.current) {
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 128;
          analyser.smoothingTimeConstant = 0.84;
          sourceRef.current.connect(analyser);
          analyser.connect(audioContext.destination);
          analyserRef.current = analyser;
        }

        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        if (!cancelled) {
          setVisualizerEnabled(true);
        }
      } catch (error) {
        if (!cancelled) {
          setVisualizerEnabled(false);
        }
      }
    };

    const handleExternalPlay = (event) => {
      if (event.detail?.audio !== audio && !audio.paused) {
        audio.pause();
      }
    };

    const animate = () => {
      const analyser = analyserRef.current;
      const audioElement = audioRef.current;

      if (analyser && audioElement && !audioElement.paused) {
        const data = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(data);
        const step = Math.max(1, Math.floor(data.length / VISUALIZER_BARS));

        barRefs.current.forEach((bar, index) => {
          if (!bar) {
            return;
          }

          const slice = data.slice(index * step, (index + 1) * step);
          const total = slice.reduce((sum, value) => sum + value, 0);
          const average = slice.length ? total / slice.length : 0;
          const scale = Math.max(0.22, average / 170);
          bar.style.transform = `scaleY(${Math.min(scale, 1)})`;
          bar.style.opacity = String(Math.min(1, 0.35 + average / 255));
        });
      } else {
        barRefs.current.forEach((bar, index) => {
          if (!bar) {
            return;
          }

          const idleScale = 0.28 + ((index % 4) * 0.08);
          bar.style.transform = `scaleY(${idleScale})`;
          bar.style.opacity = '0.42';
        });
      }

      animationFrameRef.current = window.requestAnimationFrame(animate);
    };

    audio.addEventListener('play', initializeVisualizer);
    window.addEventListener(AUDIO_PLAYER_SYNC_EVENT, handleExternalPlay);
    animationFrameRef.current = window.requestAnimationFrame(animate);

    return () => {
      cancelled = true;
      audio.removeEventListener('play', initializeVisualizer);
      window.removeEventListener(AUDIO_PLAYER_SYNC_EVENT, handleExternalPlay);

      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      if (audioContextRef.current?.state !== 'closed') {
        audioContextRef.current?.close();
      }

      audioContextRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
    };
  }, []);

  const togglePlayback = async () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    if (audio.paused) {
      try {
        await audio.play();
        window.dispatchEvent(
          new CustomEvent(AUDIO_PLAYER_SYNC_EVENT, {
            detail: { audio },
          })
        );
      } catch (error) {
        setIsPlaying(false);
        setVisualizerEnabled(false);
      }
      return;
    }

    audio.pause();
  };

  const handleSeek = (event) => {
    const audio = audioRef.current;
    const nextValue = Number(event.target.value);

    if (!audio || !Number.isFinite(nextValue)) {
      return;
    }

    audio.currentTime = nextValue;
    setCurrentTime(nextValue);
  };

  return (
    <div className="audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />

      <div className="audio-player__topline">
        <button
          type="button"
          className="audio-player__toggle"
          onClick={togglePlayback}
          aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
        >
          <span aria-hidden="true">{isPlaying ? 'II' : '▶'}</span>
        </button>

        <div className="audio-player__meta">
          <strong>{title || 'Audio track'}</strong>
          <span>{isReady ? 'Ready to play' : 'Loading audio'}</span>
        </div>

        <div
          className={`audio-player__visualizer ${visualizerEnabled ? 'is-active' : ''}`}
          aria-hidden="true"
        >
          {bars.map((_, index) => (
            <span
              key={index}
              ref={(element) => {
                barRefs.current[index] = element;
              }}
              className="audio-player__bar"
            />
          ))}
        </div>
      </div>

      <div className="audio-player__timeline">
        <span>{formatTime(currentTime)}</span>
        <input
          type="range"
          min="0"
          max={duration || 0}
          step="0.1"
          value={Math.min(currentTime, duration || 0)}
          onChange={handleSeek}
          disabled={!duration}
          style={{ '--audio-progress': `${progress}%` }}
          aria-label="Audio progress"
        />
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};

export default AudioPlayer;
